import { useSelector, getLocale } from 'umi';
import { useMemoizedFn } from 'ahooks';
import { FILTER_NAMESPACE } from '@intelligence/models/filter';
import { LANGUAGE_CN } from '@intelligence/_config/config';

import type { IGenreSource, IGenreMetadata } from '@intelligence/types/common';

interface IOptionsFormatMessageFieldsMapping {
  textEN?: string;
  textCN?: string;
  value?: string;
}

// 获取品类对应名称（翻译后的名称）
type IGetText = (locale: string, payload: Omit<IOptionsFormatMessageFieldsMapping, 'value'>) => string;

// 翻译单个品类（主、副）
type IGenreFormatMessage = (locale: string, options?: IGenreMetadata[], genre?: string) => string;

// 翻译多个品类（主、副）
type IGenresFormatMessage = (locale: string, options?: IGenreMetadata[], genres?: string) => string;

// 翻译品类数据名称（提供给页面调用的）
type IGenresDataFormatMessage = (payload: { genreSource: IGenreSource; mainGenre: string; subGenre?: string }) => {
  mainGenre: string;
  subGenre?: string;
};

const GENRE_RES_KEYS: Partial<Record<IGenreSource, 'annir' | 'ieg' | 'sensortower'>> = {
  iegg: 'ieg',
  sensortower: 'sensortower',
  annie: 'annir',
};

/**
 * 获取品类对应名称（翻译后的名称）
 */
const getText: IGetText = (locale, payload) => {
  const { textEN, textCN } = payload;
  const text = textEN || textCN || '';
  return (locale === LANGUAGE_CN ? textCN : textEN) || text;
};

/**
 * 翻译单个品类（主、副）
 * @param options 品类选项数据（主、副）
 * @param genre 品类value
 * @return 品类名称（主、副）
 */
const genreFormatMessage: IGenreFormatMessage = (locale, options, genre) => {
  if (!genre) return '';
  if (!Array.isArray(options)) return genre;
  const option = options.find((option) => option?.genre === genre);
  if (!option) return genre;
  return getText(locale, {
    textEN: option.genre_en,
    textCN: option.genre_cn,
  });
};

/**
 * 翻译多个品类（主、副）
 * @param options 品类选项数据（主、副）
 * @param genres 品类value（多个品类，用|连接的字符串）
 * @reutnr 品类名称（主、副，多个品类用|连接的字符串）
 */
const genresFormatMessage: IGenresFormatMessage = (locale, options, genres) => {
  if (!genres) return '';
  return genres
    .split('|')
    .map((genre) => genreFormatMessage(locale, options, genre))
    .join('|');
};

export default function useGenresIntl() {
  const locale = getLocale();
  const { commonGrenreFilter } = useSelector<any, any>((rootState) => rootState?.[FILTER_NAMESPACE]);

  const { loading, data } = commonGrenreFilter || {};

  /**
   * 翻译品类
   * @param genreSource 品类类型、annie或者iegg，需要用来确定品类选项数据
   * @param mainGenre 主品类value
   * @param subGenre 副品类value
   * @return 主品类、副品类 对应中英文环境下的文案
   */
  return useMemoizedFn<IGenresDataFormatMessage>((payload) => {
    const { genreSource, mainGenre, subGenre } = payload || {};
    // 品类选项数据还在加载中，不做翻译，原样返回
    if (loading) return { mainGenre, subGenre };
    const genreResKey = GENRE_RES_KEYS?.[genreSource] ?? genreSource;
    // 品类选项数据
    const genreOptionsData = data?.[genreResKey];
    // 主品类选项数据
    const mainGenreOptions = genreOptionsData?.map((genreOptionData: any) => {
      const mainGenreOption = genreOptionData?.main_genre || {};
      const mainGenreOptionSubGenreOptions = genreOptionData?.sub_genre || [];
      return {
        ...mainGenreOption,
        sub_genre: mainGenreOptionSubGenreOptions,
      };
    });
    // 主品类名称（翻译）
    let mainGenreText = '';
    // 副品类名称（翻译）
    let subGenreText = '';

    // 翻译主品类
    mainGenreText = genresFormatMessage(locale, mainGenreOptions, mainGenre);

    // 没有传入副品类，则仅翻译主品类
    if (!Reflect.has(payload || {}, 'subGenre')) return { mainGenre: mainGenreText };

    // 第一个主品类
    const firstMainGenre = mainGenre?.split('|')?.[0];
    // 第一个主品类选项
    const firstMainGenreOption = mainGenreOptions?.find((option: any) => option?.genre === firstMainGenre);
    // 第一个主品类的副品类选项数据
    const firstMainGenreSubGenreOptions = firstMainGenreOption?.sub_genre;

    /**
     * 翻译副品类，副品类是多个，由字符|连接的字符串
     * 翻译副品类时，暂时用第一个主品类下副品类选项数据翻译，因为主品类、副品类都是用|连接的字符串，所以没办法确定每一个副品类 归属于 哪一个主品类，就没办法确定应该用哪一个主品类的副品类选项数据来翻译了，所以这里暂时这样处理
     */
    subGenreText = genresFormatMessage(locale, firstMainGenreSubGenreOptions, subGenre);

    return {
      mainGenre: mainGenreText,
      subGenre: subGenreText,
    };
  });
}
